import { Modbus as ModbusSdk, Site as SiteSdk } from "coolremote-sdk";
import useMutation from "./useMutation";
import useQuery from "./useQuery";
import { IModbusDeviceTemplate } from "../screens/Modbus/types";

const oid = (v: any): string | undefined =>
  typeof v === "string" ? v : v?.$oid ?? v?._id?.$oid ?? undefined;

export const normalizeDeviceTemplate = (doc: any): IModbusDeviceTemplate => ({
  ...doc,
  id: oid(doc?._id) ?? doc?.id,
  site: oid(doc?.site),
  device: oid(doc?.device),
  units: Array.isArray(doc?.units)
    ? doc.units.map((u: any) => ({ ...u, functionalTemplate: oid(u?.functionalTemplate) ?? u?.functionalTemplate }))
    : [],
});

/**
 * BE validators are strict Joi schemas (unknown keys rejected). Send only the
 * permitted fields. Create allows `site`; update does not.
 * (validators/modbusDeviceTemplates.js)
 */
const serializeCreate = (t: IModbusDeviceTemplate) => ({
  site: t.site,
  name: t.name,
  vendorName: t.vendorName,
  modelName: t.modelName,
  description: t.description || undefined,
  timeoutMs: t.timeoutMs,
  units: t.units,
});
const serializeUpdate = (t: IModbusDeviceTemplate) => ({
  name: t.name,
  vendorName: t.vendorName,
  modelName: t.modelName,
  description: t.description || undefined,
  timeoutMs: t.timeoutMs,
  units: t.units,
});

export const useGetSiteModbusDeviceTemplates = (siteId: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: ["siteModbusDeviceTemplates", siteId],
    queryFn: async (): Promise<IModbusDeviceTemplate[]> => {
      const res = await SiteSdk.getSiteModbusDeviceTemplates(siteId);
      const dict = res?.data ?? res ?? {};
      return Object.values(dict).map(normalizeDeviceTemplate);
    },
    onSuccess: undefined,
    onError: undefined,
    enabled: enabled && !!siteId,
  });
};

export const useGetModbusDeviceTemplate = (templateId: string | undefined) => {
  return useQuery({
    queryKey: ["modbusDeviceTemplate", templateId],
    queryFn: async (): Promise<IModbusDeviceTemplate> => {
      const res = await ModbusSdk.getDeviceTemplateById(templateId as string);
      return normalizeDeviceTemplate(res?.data ?? res);
    },
    onSuccess: undefined,
    onError: undefined,
    enabled: !!templateId,
  });
};

interface IMutationOptions {
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
}

export const useCreateModbusDeviceTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async (data: IModbusDeviceTemplate) => {
      const res = await ModbusSdk.createDeviceTemplate(serializeCreate(data));
      return normalizeDeviceTemplate(res?.data ?? res);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useUpdateModbusDeviceTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async ({ templateId, data }: { templateId: string; data: IModbusDeviceTemplate }) => {
      const res = await ModbusSdk.updateDeviceTemplate(templateId, serializeUpdate(data));
      return normalizeDeviceTemplate(res?.data ?? res);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useRenameModbusDeviceTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async ({ templateId, name }: { templateId: string; name: string }) => {
      const res = await ModbusSdk.renameDeviceTemplate(templateId, name);
      return normalizeDeviceTemplate(res?.data ?? res);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const useDeleteModbusDeviceTemplate = (options: IMutationOptions = {}) => {
  return useMutation({
    mutationFn: async (templateId: string) => {
      // The DELETE body has no `.data`, so the SDK resolves `undefined`. xhr throws
      // on non-2xx, so reaching here means success — return a truthy result.
      await ModbusSdk.deleteDeviceTemplate(templateId);
      return { success: true };
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};
